import React, { useEffect, useState, useRef } from 'react'
import { UserService, LocalStorageService, MessageService } from '../../../services'
import { decryptMessage, getNavigatorTimezone, maskString } from '../../../utils'
import { PersonalInformation, TermsCondition } from '../../../assets/logos/icons'
import { useTranslation } from 'react-i18next'
import { NewUserOnboardingView } from '../../views'
import OnboardingDataUserView from '../../views/new-user-on-boarding/on-boarding-data-user.view'
import { ProfileValidator } from '../../../validators'
import OnboardingTermsConditionsView from '../../views/new-user-on-boarding/on-boarding-terms-conditions.view'

/**
 * @author Edgar Escobedo
 * @description Onboarding action component for new users who do not have a password and were not created in Auth0
 * @createdDate 07-10-2025
 * @returns {JSX.Element} The NewUserOnboardingAction component
 */
const NewUserOnboardingAction = ({ }) => {
  const { t } = useTranslation()

  const ENV = process.env
  const url = ENV.REACT_APP_MIFIRMA_API
  const userService = new UserService(url)
  const localStorageService = new LocalStorageService()
  const messageService = new MessageService(url)
  const validators = ProfileValidator(t)
  const timezone = getNavigatorTimezone() || ''
  const inputOTP = useRef(null)

  const [isLoadingView, setIsLoadingView] = useState(false)
  const [showLoadingRequesting, setShowLoadingRequesting] = useState(false)
  const [showCompletedRequesting, setShowCompletedRequesting] = useState(false)
  const [showErrorRequesting, setShowErrorRequesting] = useState(false)
  const [message, setMessage] = useState('')
  const [messageComplete, setMessageComplete] = useState('')
  const [nameComponent, setNameComponent] = useState('')
  const [email, setEmail] = useState('')
  const [emailDecrypted, setEmailDecrypted] = useState('')
  const [validationCode, setValidationCode] = useState('')
  const [focusedOTP, setFocusedOTP] = useState(false)
  const [emailMasked, setEmailMasked] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [selectedStep, setSelectedStep] = useState(0)
  const [otpAttempts, setOtpAttempts] = useState(0)
  const [isAccountBlocked, setIsAccountBlocked] = useState(false)
  const [blockExpiryTime, setBlockExpiryTime] = useState(null)
  const [resendAttempts, setResendAttempts] = useState(0)
  const [languageIdFromBackend, setLanguageIdFromBackend] = useState(null)
  const [dataUser, setDataUser] = useState({
    name: '',
    lastName: '',
    secondLastName: '',
    cellphoneNumber: ''
  })
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showErrorName, setShowErrorName] = useState(false)
  const [showErrorLastName, setShowErrorLastName] = useState(false)
  const [showErrorSecondLastName, setShowErrorSecondLastName] = useState(false)
  const [showErrorCellphoneNumber, setShowErrorCellphoneNumber] = useState(false)
  const [showErrorPassword, setShowErrorPassword] = useState(false)
  const [showErrorConfirmPassword, setShowErrorConfirmPassword] = useState(false)
  const [labelErrorName, setLabelErrorName] = useState('')
  const [labelErrorLastName, setLabelErrorLastName] = useState('')
  const [labelErrorSecondLastName, setLabelErrorSecondLastName] = useState('')
  const [labelErrorCellphoneNumber, setLabelErrorCellphoneNumber] = useState('')
  const [labelErrorPassword, setLabelErrorPassword] = useState('')
  const [labelErrorConfirmPassword, setLabelErrorConfirmPassword] = useState('')
  const [changePasswordShowPasswordIcon, setChangePasswordShowPasswordIcon] = useState('changePasswordView-show-password-icon-desactive')
  const [changePasswordShowPasswordConfirmIcon, setChangePasswordShowPasswordConfirmIcon] = useState('changePasswordView-show-password-icon-desactive')
  const [termsAndConditions, setTermsAndConditions] = useState(false)
  const [showErrorTerms, setShowErrorTerms] = useState(false)
  const [messageErrorTerms, setMessageErrorTerms] = useState('')
  const [showModalTermsCond, setShowModalTermsCond] = useState(false)

  /** @description Define the steps for the onboarding process
   * @type {Array<{number: number, component: JSX.Element}>}
  */
  const stepMetadata = [
    { number: 0, component: OnboardingDataUserView },
    { number: 1, component: OnboardingTermsConditionsView },
  ]

  const maskEmailGov = (email) => {
    if (!email) return ''

    const [user, domain] = email.split('@')

    return `${user.substring(0, 2)}${'*'.repeat(8)}@${'*'.repeat(8)}`
  }

  /** @description Load component and extract URL parameters */
  useEffect(() => {
    const params = getUrlParams()
    const emailParam = params.email || ''
    let emailDecrypted = ''

    if (emailParam) emailDecrypted = decryptEmail(emailParam)
    setEmailDecrypted(emailDecrypted)
    setEmail(emailParam)

    const stringMasked = maskEmailGov(emailDecrypted)
    setEmailMasked(stringMasked)

    const blockedData = localStorageService.getLocalStorageItem(`blocked_${emailDecrypted}`)
    if (blockedData && blockedData.expiryTime) {
      const now = new Date().getTime()
      if (now < blockedData.expiryTime) {
        setIsAccountBlocked(true)
        setBlockExpiryTime(blockedData.expiryTime)
        setOtpAttempts(blockedData.attempts || 3)
      } else {
        localStorageService.removeLocalStorageItem(`blocked_${emailDecrypted}`)
        setOtpAttempts(0)
      }
    }

    setComponent('validationCode')
    if (emailParam) {
      handleSendCodeOnLoad(emailParam)
    }
  }, [])


  /** @description Auto-validate when code reaches 6 digits */
  useEffect(() => {
    if (validationCode.length === 6 && !isValidating) {
      handleValidateCode()
    }
  }, [validationCode])

  /** @description Close loading view */
  const toggleCloseLoadingView = () => {
    setIsLoadingView(false)
  }

  /** @description Toggle loading request state @type {{value: boolean}} */
  const toggleLoadingRequesting = (value) => {
    setShowLoadingRequesting(value)
  }

  /** @description Toggle completed request state @type {{message: string, time: number, sendInbox: boolean, closeModal: boolean}} */
  const toggleCompletedRequesting = (message, time, sendInbox, closeModal) => {
    setShowCompletedRequesting(true)
    setMessageComplete(message || '')
    if (closeModal) {
      setTimeout(() => {
        setShowCompletedRequesting(false)
        setMessageComplete('')
        if (sendInbox) {
          window.location.replace('/')
        }
      }, time || 4000)

    }
  }

  /** @description Close completed request state */
  const toggleCloseCompletedRequesting = () => {
    setShowCompletedRequesting(false)
    setMessage('')
  }

  /** @description Toggle error request state @type {{message: string, time: number, sendInbox: boolean, closeModal: boolean}} */
  const toggleErrorRequesting = (message, time, sendInbox, closeModal) => {
    setShowErrorRequesting(true)
    setMessage(message || '')
    if (closeModal) {
      setTimeout(() => {
        setShowErrorRequesting(false)
        setMessage('')
        if (sendInbox) {
          window.location.replace('/')
        }
      }, time || 4000)
    }
  }

  /** @description Close error request state */
  const toggleCloseErrorRequesting = () => {
    setShowErrorRequesting(false)
  }

  /** @description Extract URL parameters */
  const getUrlParams = (url = (typeof window !== 'undefined' ? window.location.href : '')) => {
    if (!url) return {}

    const base = (typeof window !== 'undefined' && window.location?.origin)
    const u = new URL(url, base)

    // First, try to read regular parameters (BrowserRouter)
    let params = Object.fromEntries(u.searchParams)

    // If it comes with HashRouter type "#/user-register-invitation?email=..."
    if (u.hash) {
      const hash = u.hash.startsWith('#/') ? u.hash.slice(2) : u.hash.slice(1)
      const [path, queryString] = hash.split('?')

      // Extract params like "#/route?email=..."
      if (queryString) {
        params = {
          ...params,
          ...Object.fromEntries(new URLSearchParams(queryString))
        }
      }

      // Extract params like "#/route/email=..."
      else if (path && path.includes('=')) {
        const parts = path.split('/')
        const last = parts[parts.length - 1]
        if (last.includes('=')) {
          const [key, value] = last.split('=')
          params[key] = value
        }
      }
    }

    return params
  }


  /** 
   * @description Decrypt message  
   * @param {string} encryptedMessage - Message encrypted 
   * @returns {string} Decrypted message 
  */
  const decryptEmail = (encryptedMessage) => {
    if (!encryptedMessage || encryptedMessage === '') return ''

    try {
      const messageDecrypted = decryptMessage(encryptedMessage)
      if (messageDecrypted === '') return ''
      return messageDecrypted
    } catch (error) {
      return ''
    }
  }

  /**
   * @description Set the name of the component to be displayed 
   * @param {String} component - Name of the component to be displayed
   */
  const setComponent = (component) => {
    if (!component || component === '') {
      setNameComponent('')
    } else {
      setNameComponent(component)
    }
  }

  /**
   * @description Handle click event on the OTP input field
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const handleClickOTP = () => {
    inputOTP.current.focus()
  }

  /**
   * @description Handle focus event on the OTP input field
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const handleFocusOTP = () => {
    setFocusedOTP(true)
  }

  /**
   * @description Handle blur event on the OTP input field
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const handleBlurOTP = () => {
    setFocusedOTP(false)
  }

  /**
   * @description Handle change event on the OTP input field
   * @param {Object} e - The event object
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const handleChangeOTP = (e) => {
    const value = e.target.value
    const CODE_LENGTH = new Array(6).fill(0)

    if (validationCode.length >= CODE_LENGTH.length) return null
    const newValidationCode = (validationCode + value).slice(0, CODE_LENGTH.length)

    setValidationCode(newValidationCode)
  }

  /**
   * @description Handle key up event on the OTP input field
   * @param {Object} e - The event object
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const handleKeyUpOTP = (e) => {
    if (e.key === 'Backspace') {
      const newValidationCode = (validationCode.slice(0, validationCode.length - 1))
      setValidationCode(newValidationCode)
      setIsValidating(false)
      return
    }
  }

  /**
   *  @description Move to the previous step in the onboarding process
   * @returns {void}
   */
  const onBackStep = () => {
    if (selectedStep === 0) return
    const backStep = selectedStep - 1
    setSelectedStep(backStep)
    return window.scroll(0, 0)
  }

  /**
   * @description Move to the next step in the onboarding process
   * @returns {void}
   */
  const onNextStep = () => {
    const nextStep = selectedStep + 1
    setSelectedStep(nextStep)
    return window.scroll(0, 0)
  }

  /**
   * @description - Handle input change events
   * @param {Object} e - The event object
   * @param {String} field - Field to update in dataUser
   * @param {Object} validator - Validator filed
   * @param {Function} setError - Function to set error state
   */
  const onHandleChange = (e, field, validator, setError) => {
    const value = (field === 'cellphoneNumber')
      ? e ? e.trim() : e
      : (e && e.target && e.target.value) ? e.target.value : ''

    if (setError) {
      setError(false)
      if (field === 'name') setLabelErrorName('')
      if (field === 'lastName') setLabelErrorLastName('')
      if (field === 'secondLastName') setLabelErrorSecondLastName('')
      if (field === 'cellphoneNumber') setLabelErrorCellphoneNumber('')
    }

    if (validator && value.trim() !== '') {
      const validation = validators[validator](value.trim())
      if (validation !== '') {
        setError(true)
        if (field === 'name') setLabelErrorName(validation)
        if (field === 'lastName') setLabelErrorLastName(validation)
        if (field === 'secondLastName') setLabelErrorSecondLastName(validation)
        return
      }
    }

    if ((field === 'name' || field === 'lastName' || field === 'secondLastName') && value) {
      const CODE_LENGTH = new Array(128).fill(0)
      if (value.length >= CODE_LENGTH.length) return null
    }

    setDataUser(prevData => ({
      ...prevData,
      [field]: value
    }))
  }

  /**
   * @description Handle input change for the password field
   * @param {String} e - The event object from the input field
   * @param {Function} setValue - The function to update the password state
   * @param {Function} setError - The function to update the error state
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const onHandleChangePassword = (e, setValue, setError) => {
    setError(false)
    if (setValue === setPassword) setLabelErrorPassword('')
    if (setValue === setConfirmPassword) setLabelErrorConfirmPassword('')

    const value = (e && e.target && e.target.value) ? e.target.value.trim() : ''
    setValue(value)
  }


  /**
 * @description Toggle the visibility of the password input field
 * @returns {void}
 * @memberof NewUserOnboardingAction
 */
  const onHandleShowPassword = () => {
    let inputPassword = document.querySelector('#password')
    let classPassword = 'changePasswordView-show-password-icon-desactive'

    if (changePasswordShowPasswordIcon === 'changePasswordView-show-password-icon-desactive' && inputPassword.type === 'password') {
      inputPassword.type = 'text'
      classPassword = 'changePasswordAction-password-icon-active'
    } else {
      inputPassword.type = 'password'
      classPassword = 'changePasswordView-show-password-icon-desactive'
    }

    setChangePasswordShowPasswordIcon(classPassword)
  }

  /**
   * @description Toggle the visibility of the confirm password input field
   * @returns {void}
   * @memberof NewUserOnboardingAction
   */
  const onHandleShowPasswordConfirm = () => {
    let inputConfirmPassword = document.querySelector('#confirmPassword')
    let classPassword = 'changePasswordView-show-password-icon-desactive'

    if (changePasswordShowPasswordConfirmIcon === 'changePasswordView-show-password-icon-desactive' && inputConfirmPassword.type === 'password') {
      inputConfirmPassword.type = 'text'
      classPassword = 'changePasswordAction-password-icon-active'
    } else {
      inputConfirmPassword.type = 'password'
      classPassword = 'changePasswordView-show-password-icon-desactive'
    }

    setChangePasswordShowPasswordConfirmIcon(classPassword)
  }

  /**
 * @description Handle the action of sending the validation code to the user's email on initial load
 * @param {string} emailParam - The email parameter from the URL
 * @returns {Promise<void>}
 */
  const handleSendCodeOnLoad = async (emailParam) => {
    toggleLoadingRequesting(true)
    let messageError = t('newUserOnboardingAction.errors.send.code')
    try {
      const response = await userService.sendCodeNewUserOnboarding(emailParam, timezone)
      if (response.statusCode === 200 && response.data) {
        toggleLoadingRequesting(false)
        const jwt = response.data.token || response.data || ''
        localStorageService.setLocalStorageItem('jwtOnboarding', jwt)

        if (response.data.user) {
          setDataUser({
            name: response.data.user.firstName || '',
            lastName: response.data.user.firstLastName || '',
            secondLastName: response.data.user.secondLastName || '',
            cellphoneNumber: ''
          })

          if (response.data.user.languageId) {
            setLanguageIdFromBackend(response.data.user.languageId)
          }
        }

        toggleCompletedRequesting(t('newUserOnboardingAction.message.send.code'), 4000, false, true)
      } else {
        if (response && response.statusCode === 426) messageError = t('newUserOnboardingAction.errors.invitation.expired')
        toggleLoadingRequesting(false)
        toggleErrorRequesting(messageError, 4000, false, true)
      }

    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(messageError, 4000, false, true)
    }
  }

  /**
 * @description Handle the action of sending the validation code to the user's email
 * @param {string} email - The email address to which the validation code will be sent
 * @returns {Promise<void>}
 */
  const handleSendCode = async () => {
    toggleLoadingRequesting(true)
    let messageError = t('newUserOnboardingAction.errors.send.code')
    try {
      const response = await userService.sendCodeNewUserOnboarding(email, timezone)
      if (response.statusCode === 200 && response.data) {
        toggleLoadingRequesting(false)
        const jwt = response.data.token || response.data || ''
        localStorageService.setLocalStorageItem('jwtOnboarding', jwt)

        if (response.data.user) {
          setDataUser({
            name: response.data.user.firstName || '',
            lastName: response.data.user.firstLastName || '',
            secondLastName: response.data.user.secondLastName || '',
            cellphoneNumber: ''
          })

          if (response.data.user.languageId) {
            setLanguageIdFromBackend(response.data.user.languageId)
          }
        }

        setComponent('validationCode')
        toggleCompletedRequesting(t('newUserOnboardingAction.message.send.code'), 4000, false, true)
      } else {
        if (response && response.statusCode === 426) messageError = t('newUserOnboardingAction.errors.invitation.expired')
        toggleLoadingRequesting(false)
        toggleErrorRequesting(messageError, 4000, false, true)
      }

    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(messageError, 4000, false, true)
    }
  }

  /**
   * @description Handle the action of resending the validation code email
   * @returns {Promise<void>}
   * @memberof NewUserOnboardingAction
   */
  const handleResendEmail = async () => {
    if (resendAttempts >= 5) {
      toggleErrorRequesting(t('newUserOnboardingAction.errors.resend.limit'), 4000, false, true)
      return
    }
    toggleLoadingRequesting(true)
    let errorMessage = t('requestOTPAction.error.resend.message')
    let email = emailDecrypted
    let validationType = ENV.REACT_APP_ACCESS_CODE_OTP_VALIDATION_TYPE_NEWUSER
    let notificationType = ENV.REACT_APP_ACCESS_CODE_OTP_NOTIFICATION_TYPE_MAIL

    try {
      const sendMessage = await messageService.reSendMessageOrEmail(validationType, notificationType, email)
      if (sendMessage && sendMessage.data && Number(sendMessage.statusCode) === 200) {
        setResendAttempts(resendAttempts + 1)
        const messageComplete = t('registration.action.resendMessageEmail')
        toggleLoadingRequesting(false)
        toggleCompletedRequesting(messageComplete, 4000, false, true)
      } else {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMessage, 4000, false, true)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(errorMessage, 4000, false, true)
    }
  }

  /**
   * @description Handle the validation of the OTP code entered by the user
   * @returns {Promise<void>}
   * @memberof NewUserOnboardingAction
   */
  const handleValidateCode = async () => {
    if (isValidating) return

    if (isAccountBlocked && blockExpiryTime) {
      const now = new Date().getTime()
      const timeRemaining = blockExpiryTime - now

      if (timeRemaining > 0) {
        const hours = Math.floor(timeRemaining / (1000 * 60 * 60))
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60))
        const timeMessage = hours > 0
          ? t('newUserOnboardingAction.errors.account.blocked.hours', { hours, minutes })
          : t('newUserOnboardingAction.errors.account.blocked.minutes', { minutes })

        toggleErrorRequesting(timeMessage, 5000, false, true)
        setValidationCode('')
        return
      } else {
        setIsAccountBlocked(false)
        setBlockExpiryTime(null)
        setOtpAttempts(0)
        localStorageService.removeLocalStorageItem(`blocked_${emailDecrypted}`)
      }
    }

    setIsValidating(true)
    toggleLoadingRequesting(true)
    let messageError = t('requestOTPAction.error.validation.code')

    try {
      let validateCode = await messageService.validateCodeLogin(emailDecrypted, validationCode, ENV.REACT_APP_ACCESS_CODE_OTP_VALIDATION_TYPE_NEWUSER)
      if (!validateCode || !validateCode.data || Number(validateCode.statusCode) !== 200) throw new Error(messageError)
      if (validateCode.data.codeIsCorrect) {
        setComponent('onBoarding')
        toggleLoadingRequesting(false)
        setIsValidating(false)
        toggleCompletedRequesting(t('requestOTPAction.message.code.correct'), 4000, false, true)
      } else {
        const newAttempts = otpAttempts + 1
        setOtpAttempts(newAttempts)

        if (newAttempts >= 3) {
          const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000)
          setIsAccountBlocked(true)
          setBlockExpiryTime(expiryTime)

          localStorageService.setLocalStorageItem(`blocked_${emailDecrypted}`, {
            attempts: newAttempts,
            expiryTime: expiryTime
          })

          messageError = t('newUserOnboardingAction.errors.account.blocked.24h')
          setValidationCode('')
          toggleLoadingRequesting(false)
          setIsValidating(false)
          toggleErrorRequesting(messageError, 5000, false, true)
        } else {
          const remainingAttempts = 3 - newAttempts
          messageError = t('newUserOnboardingAction.errors.code.incorrect.attempts', { attempts: remainingAttempts })
          setValidationCode('')
          toggleLoadingRequesting(false)
          setIsValidating(false)
          toggleErrorRequesting(messageError, 4000, false, true)
        }
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      setIsValidating(false)
      toggleErrorRequesting(messageError, 4000, false, true)
    }
  }

  const handleValidateUserData = async () => {

    let hasErrors = false

    setShowErrorName(false)
    setShowErrorLastName(false)
    setShowErrorPassword(false)
    setShowErrorConfirmPassword(false)
    setLabelErrorName('')
    setLabelErrorLastName('')
    setLabelErrorPassword('')
    setLabelErrorConfirmPassword('')

    if (!dataUser.name || dataUser.name.trim() === '') {
      setLabelErrorName(t('dialogSignerList2.requiredField'))
      setShowErrorName(true)
      hasErrors = true
    } else {
      const nameValidation = validators.nameValidator(dataUser.name.trim())
      if (nameValidation !== '') {
        setLabelErrorName(nameValidation)
        setShowErrorName(true)
        hasErrors = true
      }
    }

    if (!dataUser.lastName || dataUser.lastName.trim() === '') {
      setLabelErrorLastName(t('dialogSignerList2.requiredField'))
      setShowErrorLastName(true)
      hasErrors = true
    } else {
      const lastNameValidation = validators.firstLastNameValidator(dataUser.lastName.trim())
      if (lastNameValidation !== '') {
        setLabelErrorLastName(lastNameValidation)
        setShowErrorLastName(true)
        hasErrors = true
      }
    }

    if (!password || password.trim() === '') {
      setLabelErrorPassword(t('dialogSignerList2.requiredField'))
      setShowErrorPassword(true)
      hasErrors = true
    } else {
      const passwordValidation = validators.passwordValidator(password.trim())
      if (passwordValidation !== '') {
        setLabelErrorPassword(passwordValidation)
        setShowErrorPassword(true)
        hasErrors = true
      }
    }

    if (!confirmPassword || confirmPassword.trim() === '') {
      setLabelErrorConfirmPassword(t('dialogSignerList2.requiredField'))
      setShowErrorConfirmPassword(true)
      hasErrors = true
    }

    if (password && confirmPassword && password.trim() !== confirmPassword.trim()) {
      toggleErrorRequesting(t('newUserOnboardingAction.errors.password.not.match'), 4000, false, true)
      hasErrors = true
    }

    if (dataUser.cellphoneNumber && dataUser.cellphoneNumber !== '') {
      const cellPhoneValidation = validators.cellphoneWithCodeValidator(dataUser.cellphoneNumber)
      if (cellPhoneValidation !== '') {
        setLabelErrorCellphoneNumber(t('profile.validator.phoneNumberValidator'))
        setShowErrorCellphoneNumber(true)
        hasErrors = true
      }
    }

    if (hasErrors) {
      return
    }

    onNextStep()
  }

  /**
   * @description Handle the action of saving the new user data to create the user in Auth0
   * @returns {Promise<void>}
   * @memberof NewUserOnboardingAction
   */
  const handleSaveUserData = async () => {
    if (!termsAndConditions) {
      setMessageErrorTerms(t('newUserOnboardingAction.errors.terms.required'))
      setShowErrorTerms(true)
      toggleErrorRequesting(t('newUserOnboardingAction.errors.terms.required'), 4000, false, true)
      return
    }

    toggleLoadingRequesting(true)
    let messageError = t('newUserOnboardingAction.errors.create.user.default')

    const name = dataUser.name ? dataUser.name.trim() : ''
    const lastName = dataUser.lastName ? dataUser.lastName.trim() : ''
    const secondLastName = dataUser.secondLastName ? dataUser.secondLastName.trim() : ''
    const cellphoneNumber = dataUser.cellphoneNumber || ''

    try {
      const jwtToken = localStorageService.getLocalStorageItem('jwtOnboarding')
      const response = await userService.createNewUserOnlyAuth0(jwtToken, email, name, lastName, secondLastName, cellphoneNumber, password)
      if (response && response.statusCode === 200 && response.data) {
        await localStorageService.setLocalStorageItem('token', response.data)
        await localStorageService.setLocalStorageItem('user', response.data.user)
        toggleLoadingRequesting(false)
        toggleCompletedRequesting('', 4000, true, true)
      } else {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(messageError, 4000, false, true)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(messageError, 4000, false, true)

    }
  }

  const onAcceptTermsAndConditions = (e) => {
    const value = e.value
    setTermsAndConditions(value)
    if (value) {
      setShowErrorTerms(false)
      setMessageErrorTerms('')
    }
  }

  const openModalTermsCond = (value) => {
    setShowModalTermsCond(value)
  }

  /**
 * @description Steps configuration for the onboarding process
 * @type {Array<{icon: JSX.Element, title: string, labelBtn: string, onBack: function|null, onNext: function}>}
 */
  const steps = [
    { icon: PersonalInformation, title: t('newUserOnboardingAction.labels.stepper.title.1') },
    { icon: TermsCondition, title: t('newUserOnboardingAction.labels.stepper.title.2') },
  ]

  return (
    <NewUserOnboardingView
      t={t}
      isLoadingView={isLoadingView}
      showLoadingRequesting={showLoadingRequesting}
      showCompletedRequesting={showCompletedRequesting}
      showErrorRequesting={showErrorRequesting}
      message={message}
      messageComplete={messageComplete}

      nameComponent={nameComponent}
      emailDecrypted={emailDecrypted}
      inputOTP={inputOTP}
      validationCode={validationCode}
      focusedOTP={focusedOTP}
      emailMasked={emailMasked}
      stepMetadata={stepMetadata}
      steps={steps}
      selectedStep={selectedStep}
      dataUser={dataUser}
      password={password}
      confirmPassword={confirmPassword}
      validators={validators}
      showErrorName={showErrorName}
      showErrorLastName={showErrorLastName}
      showErrorSecondLastName={showErrorSecondLastName}
      showErrorCellphoneNumber={showErrorCellphoneNumber}
      showErrorPassword={showErrorPassword}
      showErrorConfirmPassword={showErrorConfirmPassword}
      labelErrorName={labelErrorName}
      labelErrorLastName={labelErrorLastName}
      labelErrorSecondLastName={labelErrorSecondLastName}
      labelErrorCellphoneNumber={labelErrorCellphoneNumber}
      labelErrorPassword={labelErrorPassword}
      labelErrorConfirmPassword={labelErrorConfirmPassword}
      changePasswordShowPasswordIcon={changePasswordShowPasswordIcon}
      changePasswordShowPasswordConfirmIcon={changePasswordShowPasswordConfirmIcon}
      termsAndConditions={termsAndConditions}
      showErrorTerms={showErrorTerms}
      messageErrorTerms={messageErrorTerms}
      showModalTermsCond={showModalTermsCond}
      languageIdFromBackend={languageIdFromBackend}

      toggleLoadingRequesting={toggleLoadingRequesting}
      toggleCompletedRequesting={toggleCompletedRequesting}
      toggleCloseCompletedRequesting={toggleCloseCompletedRequesting}
      toggleErrorRequesting={toggleErrorRequesting}
      toggleCloseErrorRequesting={toggleCloseErrorRequesting}
      setShowErrorName={setShowErrorName}
      setShowErrorLastName={setShowErrorLastName}
      setShowErrorSecondLastName={setShowErrorSecondLastName}
      setShowErrorCellphoneNumber={setShowErrorCellphoneNumber}
      setShowErrorPassword={setShowErrorPassword}
      setShowErrorConfirmPassword={setShowErrorConfirmPassword}

      handleSendCode={handleSendCode}
      handleResendEmail={handleResendEmail}
      handleValidateCode={handleValidateCode}
      handleClickOTP={handleClickOTP}
      handleFocusOTP={handleFocusOTP}
      handleBlurOTP={handleBlurOTP}
      handleChangeOTP={handleChangeOTP}
      handleKeyUpOTP={handleKeyUpOTP}
      onHandleChange={onHandleChange}
      onHandleChangePassword={onHandleChangePassword}
      setPassword={setPassword}
      setConfirmPassword={setConfirmPassword}
      onHandleShowPassword={onHandleShowPassword}
      onHandleShowPasswordConfirm={onHandleShowPasswordConfirm}
      handleValidateUserData={handleValidateUserData}
      handleSaveUserData={handleSaveUserData}
      onBackStep={onBackStep}
      onAcceptTermsAndConditions={onAcceptTermsAndConditions}
      openModalTermsCond={openModalTermsCond}
    />
  )
}

export { NewUserOnboardingAction as default }